import { formatApiErrorDetail } from "@/lib/api";
import WritebackPanel from "@/components/ClickUpWritebackPanel";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import {
  ArrowSquareOut, CaretDown, CaretRight, CheckSquare, Square,
  ChatCircle, ClipboardText, FolderSimple, ListChecks, Users,
  ArrowClockwise, ArrowsClockwise, WifiHigh, WifiSlash, Paperclip, FileImage, File as FileIcon,
  MagnifyingGlass, MagicWand, CheckCircle, XCircle, Warning, DownloadSimple,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminClickUp — read-only ClickUp mirror.
 * Route: /admin/clickup
 *
 * 3 tabs:
 *   1. Workspace   — hierarchy tree, pick lists to include in feed
 *   2. Team Feed   — chronological comments across watched lists
 *   3. Status      — connection health + workspace stats
 */
export default function AdminClickUp() {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "workspace");   // workspace | feed | files | status | writeback
  const [build, setBuild] = useState(null);

  useEffect(() => {
    axios.get(`${BASE}/api/clickup/status`, auth())
      .then(r => setStatus(r.data))
      .catch(e => setStatus({ connected: false, reason: e?.response?.data?.detail || "error" }));
    axios.get(`${BASE}/api/version`).then(r => setBuild(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="admin-clickup-page">
        <div className="flex items-center gap-3">
          <ClipboardText weight="fill" size={30} className="text-indigo-600"/>
          <div>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
              ClickUp Bridge
            </h1>
            <div className="text-sm text-slate-600 font-medium mt-0.5">
              Mirror ClickUp into Field Radar; optional one-way Write-Back pushes WO status, comments and photos to your tasks (see Write-Back tab).
            </div>
          </div>
          <div className="ml-auto flex flex-col items-end gap-1">
            {status?.connected
              ? <StatusPill icon={WifiHigh} label={`${status.workspace_count} workspace`} color="emerald"/>
              : <StatusPill icon={WifiSlash} label="Not connected" color="rose"/>}
            {build?.built && (
              <span data-testid="build-chip"
                title="Backend build timestamp — should match your last deploy time"
                className="inline-flex items-center h-5 px-2 rounded-sm bg-slate-900 text-emerald-300 text-[9px] font-mono font-bold">
                BUILD · {new Date(build.built).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                {build.features?.clickup_custom_field_files && <span className="ml-1 text-emerald-400">✓ CF</span>}
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 inline-flex border-2 border-slate-200 bg-slate-50 flex-wrap">
          {[
            { k: "workspace", label: "Workspace",   icon: FolderSimple },
            { k: "feed",      label: "Team Feed",   icon: ChatCircle },
            { k: "files",     label: "Files",       icon: Paperclip },
            { k: "attach",    label: "Auto-Attach", icon: MagicWand },
            { k: "mirror",    label: "Mirror",      icon: ArrowsClockwise },
            { k: "writeback", label: "Write-Back",  icon: ArrowsClockwise },
            { k: "missing",   label: "Pull Missing", icon: DownloadSimple },
            { k: "status",    label: "Status",      icon: WifiHigh },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              data-testid={`clickup-tab-${t.k}`}
              className={`h-9 px-3 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 ${
                tab === t.k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}>
              <t.icon size={12} weight="fill"/> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {tab === "workspace" && status?.connected && (
            <WorkspaceBrowser teamId={status.workspaces[0]?.id}/>
          )}
          {tab === "feed" && status?.connected && <TeamFeed/>}
          {tab === "files" && status?.connected && <FilesFeed onOpenTask={() => {}}/>}
          {tab === "attach" && status?.connected && <AutoAttachPanel/>}
          {tab === "mirror" && status?.connected && <MirrorPanel/>}
          {tab === "writeback" && status?.connected && <WritebackPanel/>}
          {tab === "missing" && status?.connected && <PullMissingPanel/>}
          {tab === "status" && <StatusPanel status={status}/>}
          {!status?.connected && (
            <div className="p-6 border-2 border-dashed border-rose-300 bg-rose-50 text-center">
              <div className="font-black text-sm uppercase tracking-widest text-rose-800">
                Not Connected
              </div>
              <div className="text-xs text-rose-700 mt-1">
                {status?.reason || "Loading…"} — check backend has CLICKUP_PERSONAL_TOKEN.
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}


function StatusPill({ icon: Icon, label, color }) {
  const map = {
    emerald: "border-emerald-400 bg-emerald-50 text-emerald-800",
    rose:    "border-rose-400   bg-rose-50   text-rose-800",
  };
  return (
    <div className={`inline-flex items-center gap-1 border-2 ${map[color]} px-2 py-0.5 font-black uppercase text-[10px] tracking-widest`}>
      <Icon size={12} weight="fill"/> {label}
    </div>
  );
}


function WorkspaceBrowser({ teamId }) {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const [tree, setTree] = useState(null);
  const [watched, setWatched] = useState(new Set());
  const [openTask, setOpenTask] = useState(null);
  const [savingWatch, setSavingWatch] = useState(false);

  useEffect(() => {
    if (!teamId) return;
    axios.get(`${BASE}/api/clickup/hierarchy/${teamId}`, auth()).then(r => setTree(r.data)).catch(() => {});
    axios.get(`${BASE}/api/clickup/watchlist`, auth()).then(r => setWatched(new Set(r.data.list_ids || []))).catch(() => {});
    /* eslint-disable-next-line */
  }, [teamId]);

  const toggleWatch = (listId) => {
    const next = new Set(watched);
    if (next.has(listId)) next.delete(listId); else next.add(listId);
    setWatched(next);
    setSavingWatch(true);
    axios.post(`${BASE}/api/clickup/watchlist`, { list_ids: Array.from(next) }, auth())
      .then(() => toast.success(`Watchlist: ${next.size} list(s)`))
      .catch(e => toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")))
      .finally(() => setSavingWatch(false));
  };

  if (!tree) return <div className="text-slate-500 font-bold">Loading workspace…</div>;

  return (
    <div className="space-y-2" data-testid="clickup-workspace-tree">
      <div className="text-xs text-slate-600 mb-2">
        Tap the ☑︎ next to any list to include it in your <b>Team Feed</b>.
        {savingWatch && <span className="ml-2 text-slate-400">saving…</span>}
      </div>
      {tree.spaces.map(sp => (
        <SpaceNode key={sp.id} space={sp} watched={watched} onToggleWatch={toggleWatch}
          onOpenTask={setOpenTask}/>
      ))}
      {openTask && (
        <TaskDrawer taskId={openTask} onClose={() => setOpenTask(null)}/>
      )}
    </div>
  );
}


function SpaceNode({ space, watched, onToggleWatch, onOpenTask }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-2 border-slate-200" data-testid={`space-${space.id}`}>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 p-2 bg-slate-50 hover:bg-slate-100 text-left">
        {open ? <CaretDown size={14}/> : <CaretRight size={14}/>}
        <FolderSimple size={14} weight="fill" className="text-indigo-500"/>
        <span className="font-black text-sm">{space.name}</span>
        <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500">
          {space.folders.length} folder · {space.lists.length} list
        </span>
      </button>
      {open && (
        <div className="border-t-2 border-slate-100 divide-y divide-slate-100">
          {space.folders.map(f => (
            <FolderNode key={f.id} folder={f} watched={watched} onToggleWatch={onToggleWatch} onOpenTask={onOpenTask}/>
          ))}
          {space.lists.map(l => (
            <ListNode key={l.id} list={l} watched={watched} onToggleWatch={onToggleWatch} onOpenTask={onOpenTask}/>
          ))}
        </div>
      )}
    </div>
  );
}


function FolderNode({ folder, watched, onToggleWatch, onOpenTask }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 p-2 pl-6 hover:bg-slate-50 text-left">
        {open ? <CaretDown size={12}/> : <CaretRight size={12}/>}
        <FolderSimple size={12} weight="regular" className="text-slate-400"/>
        <span className="font-bold text-sm">{folder.name}</span>
        <span className="ml-auto text-[9px] font-black uppercase tracking-widest text-slate-400">
          {folder.lists.length} list
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {folder.lists.map(l => (
            <ListNode key={l.id} list={l} watched={watched}
              onToggleWatch={onToggleWatch} onOpenTask={onOpenTask} indent={12}/>
          ))}
        </div>
      )}
    </div>
  );
}


function ListNode({ list, watched, onToggleWatch, onOpenTask, indent = 6 }) {
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState(null);
  const isWatched = watched.has(list.id);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const loadTasks = () => {
    if (tasks !== null) return;
    axios.get(`${BASE}/api/clickup/lists/${list.id}/tasks`, { ...auth(), params: { limit: 25 } })
      .then(r => setTasks(r.data.tasks || []))
      .catch(() => setTasks([]));
  };

  return (
    <div>
      <div className={`flex items-center gap-2 p-2 hover:bg-indigo-50 ${isWatched ? "bg-indigo-50/50" : ""}`}
        style={{ paddingLeft: `${indent * 4}px` }}>
        <button onClick={(e) => { e.stopPropagation(); onToggleWatch(list.id); }}
          data-testid={`watch-toggle-${list.id}`}
          className="w-5 h-5 flex items-center justify-center">
          {isWatched
            ? <CheckSquare weight="fill" size={16} className="text-indigo-600"/>
            : <Square weight="regular" size={16} className="text-slate-400"/>}
        </button>
        <button onClick={() => { const nx = !open; setOpen(nx); if (nx) loadTasks(); }}
          className="flex-1 text-left flex items-center gap-2">
          <ListChecks size={12} weight="bold" className="text-slate-500"/>
          <span className="text-sm font-medium">{list.name}</span>
          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
            {list.task_count ?? "?"} task
          </span>
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-100 bg-slate-50" style={{ paddingLeft: `${indent * 4 + 20}px` }}>
          {tasks === null && <div className="p-2 text-xs text-slate-400">Loading…</div>}
          {tasks && tasks.length === 0 && <div className="p-2 text-xs text-slate-400">No tasks</div>}
          {tasks && tasks.length > 0 && (
            <div className="divide-y divide-slate-100">
              {tasks.map(t => (
                <button key={t.id} onClick={() => onOpenTask(t.id)}
                  data-testid={`task-row-${t.id}`}
                  className="w-full text-left p-2 hover:bg-white flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full flex-none"
                    style={{ background: t.status_color || "#94a3b8" }}/>
                  <span className="text-xs font-medium truncate flex-1">{t.name}</span>
                  {t.assignees && t.assignees.length > 0 && (
                    <span className="text-[9px] text-slate-500 flex-none">
                      {t.assignees.slice(0, 2).map(a => a.name).join(", ")}
                    </span>
                  )}
                  {t.status && (
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 flex-none">
                      {t.status}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function TaskDrawer({ taskId, onClose }) {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${BASE}/api/clickup/tasks/${taskId}`, auth())
      .then(r => setData(r.data))
      .catch(() => setData({ error: true }));
    /* eslint-disable-next-line */
  }, [taskId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}>
      <div className="bg-white border-2 border-slate-900 max-w-2xl w-full max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="clickup-task-drawer">
        <div className="p-4 border-b-2 border-slate-200 flex items-center gap-2 sticky top-0 bg-white z-10">
          <ClipboardText weight="fill" size={16} className="text-indigo-500"/>
          <div className="font-black uppercase text-[11px] tracking-widest">Task</div>
          {data?.task?.url && (
            <a href={data.task.url} target="_blank" rel="noreferrer"
              className="ml-auto h-8 px-2 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              Open in ClickUp <ArrowSquareOut size={12}/>
            </a>
          )}
          <button onClick={onClose}
            className="h-8 w-8 border-2 border-slate-300 hover:bg-slate-100 font-black">×</button>
        </div>
        {!data && <div className="p-6 text-center text-slate-500">Loading…</div>}
        {data?.error && <div className="p-6 text-center text-rose-700 font-bold">Failed to load task.</div>}
        {data?.task && (
          <div className="p-4 space-y-3">
            <div className="font-black text-lg">{data.task.name}</div>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              {data.task.status && (
                <span className="inline-flex items-center gap-1 border-2 px-2 py-0.5 font-black uppercase text-[10px] tracking-widest"
                  style={{ borderColor: data.task.status_color || "#cbd5e1", color: data.task.status_color || "#475569" }}>
                  {data.task.status}
                </span>
              )}
              {data.task.priority && (
                <span className="border-2 border-amber-400 bg-amber-50 text-amber-900 px-2 py-0.5 font-black uppercase text-[10px] tracking-widest">
                  {data.task.priority}
                </span>
              )}
              {data.task.due_date && (
                <span className="text-slate-600">Due {new Date(data.task.due_date).toLocaleDateString()}</span>
              )}
              <span className="ml-auto text-slate-400">{data.task.list_name}</span>
            </div>
            {data.task.text_content && (
              <div className="text-sm text-slate-700 whitespace-pre-wrap border-l-2 border-slate-200 pl-3">
                {data.task.text_content}
              </div>
            )}
            {data.task.attachments && data.task.attachments.length > 0 && (
              <div className="pt-2 border-t-2 border-dashed border-slate-200" data-testid="task-attachments">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-2 flex items-center gap-1">
                  <Paperclip size={12} weight="fill" className="text-fuchsia-600"/>
                  Attachments ({data.task.attachments.length})
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {data.task.attachments.map(a => <AttachmentCard key={a.id} a={a}/>)}
                </div>
              </div>
            )}
            {data.task.assignees && data.task.assignees.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                <Users size={12} className="text-slate-500"/>
                {data.task.assignees.map(a => (
                  <span key={a.id} className="text-[10px] border border-slate-300 bg-slate-50 px-2 py-0.5 rounded-full">
                    {a.name}
                  </span>
                ))}
              </div>
            )}
            <div className="pt-2 border-t-2 border-dashed border-slate-200">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-2">
                Comments ({data.comments.length})
              </div>
              {data.comments.length === 0 && (
                <div className="text-xs text-slate-400">No comments yet.</div>
              )}
              <div className="space-y-2">
                {data.comments.map(c => (
                  <div key={c.id} className="p-2 border border-slate-200 bg-slate-50">
                    <div className="flex items-center gap-2 text-[10px] text-slate-500 mb-1">
                      <span className="font-black">{c.user || "Unknown"}</span>
                      {c.date && <span>· {new Date(parseInt(c.date, 10)).toLocaleString()}</span>}
                      {c.resolved && <span className="ml-auto text-emerald-700 font-black">RESOLVED</span>}
                    </div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{c.text || "(empty)"}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function TeamFeed() {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    axios.get(`${BASE}/api/clickup/feed`, { ...auth(), params: { limit: 60 } })
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Feed load failed")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!data) return <div className="text-slate-500 font-bold">Loading feed…</div>;
  if (data.hint === "no_watchlist") {
    return (
      <div className="p-6 border-2 border-dashed border-amber-300 bg-amber-50 text-center">
        <ChatCircle size={44} weight="fill" className="mx-auto mb-2 text-amber-500"/>
        <div className="font-black text-sm uppercase tracking-widest text-amber-800">Pick lists first</div>
        <div className="text-xs text-amber-700 mt-1">
          Go to the <b>Workspace</b> tab and check the ☑︎ box next to any lists you want in this feed
          (e.g. Turns, Move In Work, AF Work Orders).
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="clickup-team-feed">
      <div className="flex items-center gap-2">
        <div className="text-xs text-slate-600">
          Last {data.comments.length} comments across your watched lists.
          {data.cached && <span className="ml-1 text-slate-400">(cached)</span>}
        </div>
        <button onClick={load} disabled={loading}
          data-testid="feed-refresh"
          className="ml-auto h-8 px-2 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
          <ArrowClockwise size={12}/> {loading ? "…" : "Refresh"}
        </button>
      </div>
      {data.comments.length === 0 && (
        <div className="p-6 text-center text-slate-400 text-sm">No recent comments.</div>
      )}
      {data.comments.map(entry => (
        <div key={`${entry.task_id}-${entry.comment.id}`}
          className="p-3 border-2 border-slate-200 bg-white"
          data-testid={`feed-entry-${entry.comment.id}`}>
          <div className="flex items-center gap-2 text-xs">
            <span className="font-black text-slate-900">{entry.comment.user || "Unknown"}</span>
            {entry.comment.date && (
              <span className="text-slate-500">
                · {new Date(parseInt(entry.comment.date, 10)).toLocaleString()}
              </span>
            )}
            <a href={entry.task_url} target="_blank" rel="noreferrer"
              className="ml-auto text-[10px] text-indigo-600 hover:underline inline-flex items-center gap-1">
              {entry.task_name} <ArrowSquareOut size={10}/>
            </a>
          </div>
          <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{entry.comment.text || "(empty)"}</div>
        </div>
      ))}
    </div>
  );
}


function AttachmentCard({ a }) {
  const isImg = a.is_image && a.thumbnail;
  const kb = a.size ? Math.round(a.size / 1024) : null;
  return (
    <a href={a.url_w_query || a.url} target="_blank" rel="noreferrer"
      data-testid={`attachment-${a.id}`}
      className="block border-2 border-slate-200 hover:border-fuchsia-400 hover:shadow bg-white transition">
      <div className="aspect-video bg-slate-50 flex items-center justify-center overflow-hidden">
        {isImg ? (
          <img src={a.thumbnail} alt={a.title} loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}/>
        ) : (
          <div className="flex flex-col items-center gap-1 text-slate-400">
            <FileIcon size={28} weight="regular"/>
            <span className="text-[9px] font-black uppercase tracking-widest">{a.extension || "file"}</span>
          </div>
        )}
      </div>
      <div className="p-1.5">
        <div className="text-[10px] font-bold text-slate-800 truncate" title={a.title}>{a.title}</div>
        <div className="text-[9px] text-slate-500 flex items-center gap-1">
          {a.extension && <span className="uppercase font-black">{a.extension}</span>}
          {kb !== null && <span>· {kb > 1024 ? `${(kb / 1024).toFixed(1)}MB` : `${kb}KB`}</span>}
        </div>
      </div>
    </a>
  );
}


function FilesFeed() {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [openTask, setOpenTask] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${BASE}/api/clickup/files`, { ...auth(), params: { limit: 100, q: query || undefined } })
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "File feed load failed")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!data) return <div className="text-slate-500 font-bold">Loading files…</div>;
  if (data.hint === "no_watchlist") {
    return (
      <div className="p-6 border-2 border-dashed border-amber-300 bg-amber-50 text-center" data-testid="files-empty-hint">
        <Paperclip size={44} weight="fill" className="mx-auto mb-2 text-amber-500"/>
        <div className="font-black text-sm uppercase tracking-widest text-amber-800">Pick lists first</div>
        <div className="text-xs text-amber-700 mt-1">
          In the <b>Workspace</b> tab, check the ☑︎ next to lists like <i>PMR Turns</i> or <i>Move In Work</i>
          so their attachments show up here.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="clickup-files-feed">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Search by filename, task, or list…"
            data-testid="files-search-input"
            className="w-full h-9 pl-7 pr-3 border-2 border-slate-300 text-sm font-medium"
          />
        </div>
        <button onClick={load} disabled={loading}
          data-testid="files-refresh"
          className="h-9 px-3 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
          <ArrowClockwise size={12}/> {loading ? "…" : "Refresh"}
        </button>
      </div>

      <div className="text-xs text-slate-600" data-testid="files-count">
        {data.files.length} of {data.total} file{data.total === 1 ? "" : "s"} across watched lists
        {data.cached && <span className="ml-1 text-slate-400">(cached)</span>}
      </div>

      {data.files.length === 0 && (
        <div className="p-8 text-center text-slate-400 text-sm border-2 border-dashed border-slate-200">
          No attachments found{query ? ` matching "${query}"` : ""}.
        </div>
      )}

      {data.files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="files-grid">
          {data.files.map(entry => (
            <div key={`${entry.task_id}-${entry.attachment.id}`}
              className="border-2 border-slate-200 bg-white flex flex-col"
              data-testid={`file-card-${entry.attachment.id}`}>
              <a href={entry.attachment.url_w_query || entry.attachment.url}
                target="_blank" rel="noreferrer"
                className="block bg-slate-50 aspect-video overflow-hidden hover:bg-slate-100">
                {entry.attachment.is_image && entry.attachment.thumbnail ? (
                  <img src={entry.attachment.thumbnail} alt={entry.attachment.title}
                    loading="lazy" className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                    <FileIcon size={32}/>
                    <span className="text-[10px] font-black uppercase tracking-widest mt-1">
                      {entry.attachment.extension || "file"}
                    </span>
                  </div>
                )}
              </a>
              <div className="p-2 flex-1 flex flex-col">
                <div className="text-xs font-black text-slate-900 truncate" title={entry.attachment.title}>
                  {entry.attachment.title}
                </div>
                <button onClick={() => setOpenTask(entry.task_id)}
                  className="text-[10px] text-indigo-700 hover:underline text-left truncate mt-0.5"
                  title={entry.task_name}>
                  📎 {entry.task_name}
                </button>
                <div className="text-[9px] text-slate-500 mt-auto pt-1 flex items-center gap-1">
                  <span className="truncate">{entry.list_name}</span>
                  {entry.attachment.date && (
                    <span className="ml-auto flex-none">
                      {new Date(entry.attachment.date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {openTask && <TaskDrawer taskId={openTask} onClose={() => setOpenTask(null)}/>}
    </div>
  );
}


function AutoAttachPanel() {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [addressFallback, setAddressFallback] = useState(true);
  const [includeVideos, setIncludeVideos] = useState(false);
  const [sinceDate, setSinceDate] = useState("");   // YYYY-MM-DD
  const [limitPerList, setLimitPerList] = useState(50);
  // task_id -> Set of attachment_ids to skip (unchecked)
  const [excluded, setExcluded] = useState({});

  const runScan = async () => {
    setScanning(true);
    setResult(null);
    setExcluded({});
    try {
      const body = {
        address_fallback: addressFallback,
        include_videos: includeVideos,
        limit_per_list: limitPerList,
      };
      if (sinceDate) {
        // Convert local date at 00:00 to epoch ms
        body.since_ms = new Date(sinceDate + "T00:00:00").getTime();
      }
      const r = await axios.post(`${BASE}/api/clickup/attach/scan`, body, auth());
      setScan(r.data);
      const total = (r.data?.stats?.files_to_copy) || 0;
      if (r.data?.hint === "no_watchlist") {
        toast.warning("Pick lists on the Workspace tab first");
      } else if (total === 0) {
        toast.info("Scan complete — no new files to copy");
      } else {
        toast.success(`Scan found ${total} file(s) ready to copy`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Scan failed"));
    } finally {
      setScanning(false);
    }
  };

  const toggleExclude = (taskId, attId) => {
    setExcluded(prev => {
      const next = { ...prev };
      const s = new Set(next[taskId] || []);
      if (s.has(attId)) s.delete(attId); else s.add(attId);
      next[taskId] = s;
      return next;
    });
  };

  const buildPairs = (matches) => {
    return matches.map(m => {
      const skip = excluded[m.task_id] || new Set();
      const ids = m.attachments
        .filter(a => a.action === "copy" && !skip.has(a.attachment_id))
        .map(a => a.attachment_id);
      return ids.length ? { task_id: m.task_id, wo_id: m.wo_id, attachment_ids: ids } : null;
    }).filter(Boolean);
  };

  const applyMatch = async (match) => {
    const pairs = buildPairs([match]);
    if (!pairs.length) return toast.info("Nothing selected for that task");
    setApplying(true);
    try {
      const r = await axios.post(`${BASE}/api/clickup/attach/apply`,
        { pairs, include_videos: includeVideos }, auth());
      toast.success(`Copied ${r.data.copied} · skipped ${r.data.skipped} · failed ${r.data.failed}`);
      await runScan();  // refresh — copied files will now show as "already copied"
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Apply failed"));
    } finally {
      setApplying(false);
    }
  };

  const applyAll = async () => {
    if (!scan?.matches) return;
    const pairs = buildPairs(scan.matches);
    if (!pairs.length) return toast.info("Nothing to apply — everything already skipped");
    setApplying(true);
    setResult(null);
    try {
      const r = await axios.post(`${BASE}/api/clickup/attach/apply`,
        { pairs, include_videos: includeVideos }, auth());
      setResult(r.data);
      toast.success(`✓ Copied ${r.data.copied} · skipped ${r.data.skipped} · failed ${r.data.failed}`);
      await runScan();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Apply failed"));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="clickup-auto-attach-panel">
      <div className="p-4 border-2 border-fuchsia-200 bg-fuchsia-50">
        <div className="flex items-start gap-3">
          <MagicWand size={22} weight="fill" className="text-fuchsia-600 mt-0.5"/>
          <div className="flex-1 text-sm text-slate-800">
            <div className="font-black uppercase text-[11px] tracking-widest text-fuchsia-800 mb-0.5">
              Auto-Attach ClickUp Files → Work Orders
            </div>
            Scan your watched lists, match each task to its Field Radar WO by
            <b> WO# </b> (or address), and copy the attachments straight into the
            WO's photo bundle. Safe to re-run — already-copied files are skipped.
          </div>
        </div>
        <div className="mt-3 flex items-center gap-4 flex-wrap text-xs">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={addressFallback} onChange={e => setAddressFallback(e.target.checked)}
              data-testid="opt-address-fallback"/>
            <span>Address fallback (match by property when no WO#)</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={includeVideos} onChange={e => setIncludeVideos(e.target.checked)}
              data-testid="opt-include-videos"/>
            <span>Include videos (skipped by default)</span>
          </label>
          <button onClick={runScan} disabled={scanning}
            data-testid="attach-scan-btn"
            className="ml-auto h-9 px-3 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
            <MagicWand size={12} weight="fill"/> {scanning ? "Scanning…" : "Scan Now"}
          </button>
        </div>
        <div className="mt-2 flex items-center gap-4 flex-wrap text-xs">
          <label className="inline-flex items-center gap-2">
            <span className="font-black uppercase tracking-widest text-[10px] text-slate-600">Since (updated on/after)</span>
            <input type="date" value={sinceDate} onChange={e => setSinceDate(e.target.value)}
              data-testid="opt-since-date"
              className="h-8 px-2 border-2 border-slate-300 text-xs font-mono"/>
            {sinceDate && (
              <button type="button" onClick={() => setSinceDate("")}
                className="text-[10px] text-slate-500 hover:text-slate-900 underline">clear</button>
            )}
          </label>
          <label className="inline-flex items-center gap-2">
            <span className="font-black uppercase tracking-widest text-[10px] text-slate-600">Scan depth</span>
            <select value={limitPerList} onChange={e => setLimitPerList(parseInt(e.target.value, 10))}
              data-testid="opt-limit-per-list"
              className="h-8 px-2 border-2 border-slate-300 text-xs">
              <option value="25">25 newest / list</option>
              <option value="50">50 newest / list</option>
              <option value="100">100 newest / list</option>
              <option value="200">200 newest / list</option>
            </select>
          </label>
          <div className="text-[10px] text-slate-500 italic">
            Deeper scans take longer but reach older tasks (Jan 2026, etc.).
          </div>
        </div>
      </div>

      {scan?.hint === "no_watchlist" && (
        <div className="p-6 border-2 border-dashed border-amber-300 bg-amber-50 text-center" data-testid="attach-empty-hint">
          <Warning size={40} weight="fill" className="mx-auto mb-2 text-amber-500"/>
          <div className="font-black text-sm uppercase tracking-widest text-amber-800">Pick lists first</div>
          <div className="text-xs text-amber-700 mt-1">
            On the <b>Workspace</b> tab, check the ☑︎ next to lists you want the scanner to walk.
          </div>
        </div>
      )}

      {scan && !scan.hint && (
        <>
          <div className="p-3 border-2 border-slate-200 bg-white flex items-center gap-3 flex-wrap text-sm" data-testid="attach-stats">
            <div><b>{scan.stats.tasks_scanned}</b> tasks scanned</div>
            <div className="text-slate-400">·</div>
            <div><b className="text-emerald-700">{scan.stats.tasks_matched}</b> matched to WOs</div>
            <div className="text-slate-400">·</div>
            <div><b className="text-fuchsia-700">{scan.stats.files_to_copy}</b> file(s) ready</div>
            {scan.stats.bytes_to_copy > 0 && (
              <>
                <div className="text-slate-400">·</div>
                <div>{(scan.stats.bytes_to_copy / (1024 * 1024)).toFixed(1)} MB total</div>
              </>
            )}
            {scan.stats.files_to_copy > 0 && (
              <button onClick={applyAll} disabled={applying}
                data-testid="attach-apply-all-btn"
                className="ml-auto h-9 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                <CheckCircle size={12} weight="fill"/> {applying ? "Copying…" : `Apply All (${scan.stats.files_to_copy})`}
              </button>
            )}
          </div>

          {scan.matches.length === 0 && (
            <div className="p-6 text-center text-slate-500 text-sm border-2 border-dashed border-slate-200">
              No task matched a WO. Try enabling address fallback or add a WO# to the task title (e.g., "Kitchen leak — WO#3384-1").
            </div>
          )}

          {scan.matches.map(m => {
            const skip = excluded[m.task_id] || new Set();
            const ready = m.attachments.filter(a => a.action === "copy" && !skip.has(a.attachment_id)).length;
            const total = m.attachments.length;
            return (
              <div key={m.task_id} className="border-2 border-slate-200 bg-white" data-testid={`match-${m.task_id}`}>
                <div className="p-3 border-b-2 border-slate-100 bg-slate-50 flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <a href={m.task_url} target="_blank" rel="noreferrer"
                      className="text-[11px] text-indigo-700 hover:underline inline-flex items-center gap-1">
                      {m.list_name} · task <ArrowSquareOut size={10}/>
                    </a>
                    <div className="font-black text-sm text-slate-900 truncate">{m.task_name}</div>
                    <div className="mt-1 text-xs text-slate-700 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 border-2 border-emerald-500 bg-emerald-50 text-emerald-800 px-2 py-0.5 font-black uppercase text-[10px] tracking-widest">
                        → WO {m.wo_number || m.wo_id.slice(-4)}
                      </span>
                      <span className="text-slate-600 truncate">{m.wo_address}</span>
                      {m.match_reason.startsWith("addr:") && (
                        <span className="border border-amber-300 bg-amber-50 text-amber-800 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest">
                          address match
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => applyMatch(m)} disabled={applying || ready === 0}
                    data-testid={`apply-btn-${m.task_id}`}
                    className="h-8 px-3 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 flex-none">
                    <CheckCircle size={12} weight="fill"/> Attach {ready}/{total}
                  </button>
                </div>
                <div className="divide-y divide-slate-100">
                  {m.attachments.map(a => {
                    const isSkipped = a.action === "skip";
                    const isExcluded = skip.has(a.attachment_id);
                    return (
                      <div key={a.attachment_id}
                        className={`p-2 flex items-center gap-3 text-xs ${isSkipped ? "bg-slate-50 text-slate-500" : ""}`}
                        data-testid={`plan-${a.attachment_id}`}>
                        {!isSkipped ? (
                          <button onClick={() => toggleExclude(m.task_id, a.attachment_id)}
                            className="flex-none">
                            {isExcluded
                              ? <Square weight="regular" size={16} className="text-slate-400"/>
                              : <CheckSquare weight="fill" size={16} className="text-fuchsia-600"/>}
                          </button>
                        ) : (
                          <XCircle size={14} weight="fill" className="text-slate-400 flex-none"/>
                        )}
                        <span className="font-mono text-[10px] uppercase text-slate-500 flex-none w-10">{a.extension || "—"}</span>
                        <span className={`flex-1 truncate ${isExcluded ? "line-through" : ""}`} title={a.title}>{a.title}</span>
                        <span className="text-[10px] text-slate-500 flex-none">
                          {a.size ? `${(a.size / 1024).toFixed(0)} KB` : "?"}
                        </span>
                        {a.action === "skip" && (
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex-none">
                            {a.reason}
                          </span>
                        )}
                        {a.target_field && a.action === "copy" && (
                          <span className="text-[9px] font-black uppercase tracking-widest text-fuchsia-700 flex-none">
                            → {a.target_field.replace("_", " ")}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {result && (
            <div className="p-3 border-2 border-emerald-400 bg-emerald-50 text-sm text-emerald-900" data-testid="attach-result">
              <div className="font-black uppercase text-[10px] tracking-widest">Apply complete</div>
              Copied <b>{result.copied}</b> · Skipped <b>{result.skipped}</b> · Failed <b>{result.failed}</b>
            </div>
          )}
        </>
      )}
    </div>
  );
}


function PullMissingPanel() {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [importing, setImporting] = useState(false);
  const [autoOn, setAutoOn] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [onlyWithWo, setOnlyWithWo] = useState(true);

  useEffect(() => {
    axios.get(`${BASE}/api/clickup/auto-create-missing`, { headers: authHeader() })
      .then(r => setAutoOn(!!r.data?.enabled))
      .catch(() => {});
  }, []);

  const scan = async (opts = {}) => {
    setScanning(true);
    setSelected(new Set());
    try {
      const filter = opts.onlyWithWo !== undefined ? opts.onlyWithWo : onlyWithWo;
      const r = await axios.get(
        `${BASE}/api/clickup/missing-tasks?only_with_wo=${filter}`,
        { headers: authHeader() });
      setResult(r.data);
      if (r.data?.hint === "no_watchlist") {
        toast.error("Pick lists on the Workspace tab first.");
      } else {
        const hidden = r.data.hidden_no_wo ? ` · ${r.data.hidden_no_wo} address-only hidden` : "";
        toast.success(`Scanned ${r.data.checked} · ${r.data.missing.length} missing${hidden}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const toggleFilter = () => {
    const next = !onlyWithWo;
    setOnlyWithWo(next);
    if (result) scan({ onlyWithWo: next });
  };

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAll = () => setSelected(new Set((result?.missing || []).map(t => t.task_id)));
  const clearAll = () => setSelected(new Set());

  const runImport = async () => {
    if (selected.size === 0) { toast.error("Pick tasks first"); return; }
    setImporting(true);
    try {
      const r = await axios.post(`${BASE}/api/clickup/import-missing`,
        { task_ids: Array.from(selected) },
        { headers: authHeader() });
      toast.success(`Imported ${r.data.created_count}${r.data.skipped_count ? ` · skipped ${r.data.skipped_count}` : ""}`);
      await scan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const toggleAuto = async () => {
    setAutoBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/clickup/auto-create-missing`,
        { enabled: !autoOn },
        { headers: authHeader() });
      setAutoOn(!!r.data?.enabled);
      toast.success(r.data?.enabled
        ? "Auto-create ON — new ClickUp tasks will land in FR every 2 min"
        : "Auto-create OFF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setAutoBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="clickup-missing-panel">
      <div className="p-4 border-2 border-fuchsia-200 bg-fuchsia-50">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-black text-sm uppercase tracking-widest text-fuchsia-900">
              🔗 Pull Missing from ClickUp
            </div>
            <div className="text-xs text-fuchsia-800 mt-1 max-w-xl">
              Finds every ClickUp task in your watched lists that has no matching Field Radar WO.
              Pick which ones to import — new WOs land pre-populated with the ClickUp WO#, title, and address.
            </div>
          </div>
          <button onClick={scan} disabled={scanning}
            data-testid="clickup-missing-scan"
            className="h-9 px-4 bg-fuchsia-700 hover:bg-fuchsia-800 disabled:opacity-40 text-white text-[11px] font-black uppercase tracking-widest">
            {scanning ? "Scanning…" : (result ? "↻ Rescan" : "🔍 Scan Now")}
          </button>
        </div>
      </div>

      <div className="p-4 border-2 border-slate-200 bg-white flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-black text-xs uppercase tracking-widest text-slate-800">
            Auto-create missing WOs on every poll
          </div>
          <div className="text-xs text-slate-500 mt-0.5 max-w-xl">
            When ON, the every-2-min poll auto-creates a Field Radar WO for any ClickUp task without a match.
            You never have to manually pull again — but junk tasks may sneak in.
          </div>
        </div>
        <button onClick={toggleAuto} disabled={autoBusy}
          data-testid="clickup-missing-auto-toggle"
          className={`h-9 px-4 border-2 text-[11px] font-black uppercase tracking-widest disabled:opacity-40 ${
            autoOn ? "border-emerald-700 bg-emerald-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
          }`}>
          {autoOn ? "● Auto-create ON" : "○ Auto-create OFF"}
        </button>
      </div>

      {/* Per-admin ClickUp identity binding + optional personal API token
          for two-way #my-clickup replies. Each admin binds their OWN CU
          identity — this panel edits only the current user. */}
      <ClickUpIdentityPanel/>

      {/* Re-pull ClickUp descriptions for any WO that was imported with an
          empty body (or the placeholder "Imported from ClickUp task …"). */}
      <div className="p-4 border-2 border-cyan-200 bg-cyan-50 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-black text-xs uppercase tracking-widest text-cyan-900">
            🔁 Backfill missing descriptions
          </div>
          <div className="text-xs text-cyan-800 mt-0.5 max-w-xl">
            Any WO that came in blank (or with the "Imported from ClickUp task…" placeholder) will re-fetch its
            description from ClickUp — now with markdown / rich-editor content included.
          </div>
        </div>
        <button onClick={async () => {
            if (!window.confirm("Re-pull descriptions for every WO with an empty / placeholder body? Safe to run multiple times.")) return;
            try {
              const r = await axios.post(`${BASE}/api/clickup/backfill-descriptions`, { dry_run: false },
                { headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
              toast.success(`Scanned ${r.data.scanned} · Updated ${r.data.updated_count} · Unchanged ${r.data.unchanged_count} · Failed ${r.data.failed_count}`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Backfill failed");
            }
          }}
          data-testid="clickup-backfill-descriptions"
          className="h-9 px-4 bg-cyan-700 hover:bg-cyan-800 text-white text-[11px] font-black uppercase tracking-widest">
          ↻ Backfill Now
        </button>
      </div>

      {result && result.missing.length === 0 && (
        <div className="p-6 border-2 border-emerald-200 bg-emerald-50 text-center">
          <div className="text-2xl mb-1">✅</div>
          <div className="font-black text-sm uppercase tracking-widest text-emerald-800">All Synced</div>
          <div className="text-xs text-emerald-700 mt-1">
            Checked {result.checked} tasks. {result.matched} already in Field Radar.
            {result.hidden_no_wo > 0 && (
              <> {result.hidden_no_wo} address-only tasks hidden by the filter.</>
            )}
          </div>
          {result.hidden_no_wo > 0 && (
            <button onClick={toggleFilter}
              className="mt-2 text-[10px] font-black uppercase tracking-widest text-emerald-800 hover:underline">
              Show address-only tasks anyway →
            </button>
          )}
        </div>
      )}

      {result && result.missing.length > 0 && (
        <div className="border-2 border-slate-200 bg-white">
          <div className="p-3 border-b-2 border-slate-100 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-xs font-black uppercase tracking-widest text-slate-700">
                {result.missing.length} task{result.missing.length === 1 ? "" : "s"} missing · {selected.size} selected
              </div>
              <button onClick={toggleFilter}
                data-testid="clickup-missing-filter-toggle"
                title={onlyWithWo ? "Currently hiding address-only tasks (like '448 Roxborough') · click to show all" : "Currently showing everything · click to hide address-only tasks"}
                className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 border-2 ${
                  onlyWithWo
                    ? "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-900 hover:bg-fuchsia-100"
                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                }`}>
                {onlyWithWo
                  ? `● Only tasks with WO#${result.hidden_no_wo ? ` (${result.hidden_no_wo} hidden)` : ""}`
                  : "○ Showing all"}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={selectAll} className="text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:underline">
                Select all
              </button>
              <button onClick={clearAll} className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:underline">
                Clear
              </button>
              <button onClick={runImport} disabled={importing || selected.size === 0}
                data-testid="clickup-missing-import"
                className="h-9 px-4 border-2 border-[#0f172a] bg-[#00C4CC] hover:bg-[#00a8af] text-[#0f172a] text-[11px] font-black uppercase tracking-widest disabled:opacity-40 shadow-[2px_2px_0px_#0f172a]">
                {importing ? "Importing…" : `Import ${selected.size || ""}`}
              </button>
            </div>
          </div>
          <div className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
            {result.missing.map(t => {
              const isPicked = selected.has(t.task_id);
              return (
                <label key={t.task_id}
                  data-testid={`missing-row-${t.task_id}`}
                  className={`flex items-center gap-3 p-3 cursor-pointer ${isPicked ? "bg-cyan-50" : "hover:bg-slate-50"}`}>
                  <input type="checkbox" checked={isPicked} onChange={() => toggle(t.task_id)}
                    className="h-4 w-4 accent-cyan-600"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900 truncate">{t.task_name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {t.wo_candidate && <span className="font-mono font-black text-fuchsia-700 mr-2">WO {t.wo_candidate}</span>}
                      {!t.wo_candidate && t.custom_id && <span className="font-mono font-bold text-slate-400 mr-2">CU# {t.custom_id}</span>}
                      {t.address_guess && <span className="mr-2">📍 {t.address_guess}</span>}
                      {t.cu_status && <span className="mr-2">· {t.cu_status}</span>}
                      · <span className="italic">{t.list_name}</span>
                    </div>
                  </div>
                  {t.task_url && (
                    <a href={t.task_url} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700"
                      onClick={e => e.stopPropagation()}>
                      Open ↗
                    </a>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}



function StatusPanel({ status }) {
  return (
    <div className="space-y-3" data-testid="clickup-status-panel">
      <div className="p-4 border-2 border-slate-200 bg-white">
        <div className="font-black text-xs uppercase tracking-widest text-slate-500 mb-2">Connection</div>
        <div className="text-sm">
          {status?.connected
            ? <span className="text-emerald-700 font-black">✓ Connected via Personal API Token</span>
            : <span className="text-rose-700 font-black">✗ Not connected — {status?.reason}</span>}
        </div>
      </div>
      {status?.workspaces?.map(w => (
        <div key={w.id} className="p-4 border-2 border-slate-200 bg-white">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ background: w.color || "#64748b" }}/>
            <div className="font-black text-lg">{w.name}</div>
            <div className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500">
              {w.member_count} members
            </div>
          </div>
          <div className="text-xs text-slate-500 mt-1">Team ID: {w.id}</div>
        </div>
      ))}
      <div className="p-3 border-2 border-slate-900 bg-slate-50 text-xs">
        <div className="font-black uppercase text-[10px] tracking-widest text-slate-700 mb-1">
          Privacy Guarantee
        </div>
        <div className="text-slate-700">
          Every ClickUp API call from Field Radar is <b>read-only (GET)</b>. Field Radar never creates,
          edits, or deletes anything in ClickUp. Your team sees zero trace of this connection.
        </div>
      </div>
    </div>
  );
}


/**
 * MirrorPanel — 2-way visibility control (read-only on ClickUp side).
 * Registers the webhook, shows recent mirrored events, describes what
 * gets mirrored in.
 */
function MirrorPanel() {
  const [status, setStatus] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [scanningNow, setScanningNow] = useState(false);
  const [scanNowResult, setScanNowResult] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [days, setDays] = useState(30);
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const BASE = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try {
      const r = await axios.get(`${BASE}/api/clickup/mirror/status`,
        { headers: { Authorization: `Bearer ${token()}` } });
      setStatus(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const register = async () => {
    setRegistering(true);
    try {
      await axios.post(`${BASE}/api/clickup/mirror/register`, {},
        { headers: { Authorization: `Bearer ${token()}` } });
      toast.success("Webhook (re-)registered with ClickUp");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Register failed"));
    } finally { setRegistering(false); }
  };

  const scanNow = async () => {
    setScanningNow(true);
    setScanNowResult(null);
    try {
      const r = await axios.post(
        `${BASE}/api/clickup/mirror/scan-now`,
        { limit_per_list: 50 },
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      const runId = r.data?.run_id;
      toast.success(
        `Scanning ${r.data?.lists_total || 0} watched list(s) for statuses and notes`,
      );
      let ticks = 0;
      const poll = setInterval(async () => {
        ticks += 1;
        try {
          const scan = await axios.get(
            `${BASE}/api/clickup/mirror/scan-now/status?run_id=${runId || ""}`,
            { headers: { Authorization: `Bearer ${token()}` } },
          );
          setScanNowResult(scan.data);
          if (scan.data?.status === "done" || scan.data?.status === "failed" || ticks > 360) {
            clearInterval(poll);
            setScanningNow(false);
            if (scan.data?.status === "done") {
              toast.success(
                `Scan done · ${scan.data.statuses_updated || 0} statuses · ` +
                  `${scan.data.new_notes || 0} new notes`,
              );
              load();
            }
          }
        } catch (_) {
          if (ticks > 360) {
            clearInterval(poll);
            setScanningNow(false);
          }
        }
      }, 2000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "ClickUp scan failed"));
      setScanningNow(false);
    }
  };

  const backfill = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const r = await axios.post(`${BASE}/api/clickup/mirror/backfill?days=${days}`, {},
        { headers: { Authorization: `Bearer ${token()}` } });
      const runId = r.data?.run_id;
      toast.success(`Backfill running in background · ${days}d · ${r.data.lists_total || 0} list(s)`);
      // Poll status every 5s until done (max 30 min)
      let ticks = 0;
      const poll = setInterval(async () => {
        ticks += 1;
        try {
          const s = await axios.get(`${BASE}/api/clickup/mirror/backfill/status?run_id=${runId || ""}`,
            { headers: { Authorization: `Bearer ${token()}` } });
          setBackfillResult(s.data);
          if (s.data?.status === "done" || ticks > 360) {
            clearInterval(poll);
            setBackfilling(false);
            if (s.data?.status === "done") {
              toast.success(`Backfill done · mirrored ${s.data.mirrored} · matched ${s.data.matched_to_wo}`);
              load();
            }
          }
        } catch (e) { /* keep polling on transient errors */ }
      }, 5000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Backfill failed"));
      setBackfilling(false);
    }
  };

  const registered = !!status?.webhook_id;

  return (
    <div className="space-y-4" data-testid="clickup-mirror-panel">
      <div className="p-4 border-2 border-fuchsia-200 bg-fuchsia-50/60">
        <div className="font-black uppercase tracking-widest text-[11px] text-fuchsia-800 flex items-center gap-1.5">
          <ArrowsClockwise weight="fill" size={13}/> One-way mirror · ClickUp → Field Radar
        </div>
        <div className="mt-1 text-xs text-slate-700">
          Every task status change, comment, attachment, assignee change, and
          deletion in ClickUp flows into the matched Field Radar WO within seconds.
          <b> Field Radar never writes back to ClickUp</b> — your team sees zero trace.
        </div>
      </div>

      <div
        className="border-2 border-cyan-500 bg-cyan-50 p-4"
        data-testid="clickup-sync-now-panel"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <ArrowsClockwise
            weight="fill"
            size={22}
            className={`mt-0.5 text-cyan-700 ${scanningNow ? "animate-spin" : ""}`}
          />
          <div className="min-w-0 flex-1">
            <div className="font-black uppercase tracking-widest text-[11px] text-cyan-900">
              Scan watched lists now
            </div>
            <div className="mt-1 text-xs text-slate-700">
              One pass checks the newest 50 tasks in every checked Workspace list,
              applies their current ClickUp status, and imports only notes not already
              on the matching work order.
            </div>
          </div>
          <button
            type="button"
            onClick={scanNow}
            disabled={scanningNow}
            data-testid="clickup-sync-now-button"
            className="h-10 px-4 border-2 border-cyan-800 bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1.5"
          >
            <ArrowsClockwise weight="bold" size={14}/>
            {scanningNow ? "Scanning…" : "Scan statuses + notes"}
          </button>
        </div>
        {scanNowResult && (
          <div
            className={`mt-3 border-2 bg-white p-3 text-xs ${
              scanNowResult.status === "failed" ? "border-rose-300" : "border-cyan-200"
            }`}
            data-testid="clickup-sync-now-result"
          >
            <div className="font-black uppercase tracking-widest text-[10px] text-slate-600">
              {scanNowResult.status === "done" ? "Scan complete" : "Scan running"}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-slate-700">
              <span>{scanNowResult.lists_scanned || 0}/{scanNowResult.lists_total || 0} lists</span>
              <span>{scanNowResult.tasks_scanned || 0} tasks checked</span>
              <span>{scanNowResult.tasks_matched || 0} WO matches</span>
              <span>{scanNowResult.statuses_updated || 0} statuses updated</span>
              <span>{scanNowResult.new_notes || 0} new notes</span>
            </div>
            {scanNowResult.errors?.length > 0 && (
              <div className="mt-2 text-[10px] text-rose-700">
                {scanNowResult.errors.join(" · ")}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-4 border-2 border-slate-200 bg-white">
        <div className="flex items-center gap-2 mb-2">
          <div className={`inline-flex items-center gap-1.5 border-2 px-2.5 py-1 font-black uppercase text-[10px] tracking-widest ${
            registered ? "bg-emerald-600 border-emerald-700 text-white" : "bg-slate-300 border-slate-400 text-slate-700"
          }`}>
            <div className={`w-2 h-2 ${registered ? "bg-white/70 animate-pulse" : "bg-slate-500"}`}/>
            {registered ? "MIRROR LIVE" : "NOT REGISTERED"}
          </div>
          <button onClick={register} disabled={registering}
            data-testid="clickup-mirror-register"
            className="ml-auto h-8 px-3 border-2 border-fuchsia-500 hover:bg-fuchsia-50 text-fuchsia-700 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
            <ArrowClockwise weight="bold" size={11}/> {registering ? "Registering…" : registered ? "Re-register" : "Register webhook"}
          </button>
        </div>
        {status?.webhook_registered_at && (
          <div className="text-[11px] text-slate-500">
            Registered {new Date(status.webhook_registered_at).toLocaleString()}. Also polled every 2 min as safety net.
          </div>
        )}
      </div>

      {/* Historical backfill */}
      <div className="p-4 border-2 border-amber-200 bg-amber-50/40" data-testid="clickup-backfill-panel">
        <div className="flex items-center gap-2 mb-2">
          <div className="font-black uppercase text-[11px] tracking-widest text-amber-900 flex-1">
            📚 Historical backfill · pull past N days of ClickUp activity
          </div>
        </div>
        <div className="text-xs text-slate-700 mb-3">
          Reads every task + comment on your watchlist that was updated in the last N days
          and mirrors them here (via read-only GET). One-shot import — no ClickUp side effects.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Days</label>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}
                  data-testid="clickup-backfill-days"
                  className="h-9 px-2 border-2 border-slate-300 rounded font-black text-sm">
            <option value={7}>7</option>
            <option value={14}>14</option>
            <option value={30}>30</option>
            <option value={60}>60</option>
            <option value={90}>90</option>
          </select>
          <button onClick={backfill} disabled={backfilling}
                  data-testid="clickup-backfill-btn"
                  className="h-9 px-4 border-2 border-slate-900 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 shadow-[2px_2px_0px_#0f172a] rounded">
            {backfilling ? "Backfilling…" : `📚 Pull last ${days} days`}
          </button>
          <button
            data-testid="clickup-recascade-btn"
            onClick={async () => {
              if (!window.confirm("Re-cascade every stored ClickUp status to ALL matching FR WOs (including twin duplicates)? Read-only against ClickUp.")) return;
              try {
                toast.info("Re-cascade running…");
                const r = await axios.post(`${BASE}/api/clickup/mirror/recascade-all`, {},
                  { headers: { Authorization: `Bearer ${token()}` } });
                toast.success(`Re-cascade done · scanned ${r.data.tasks_scanned} · updated ${r.data.wos_updated} WOs`);
                load();
              } catch (e) {
                toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Re-cascade failed"));
              }
            }}
            className="h-9 px-3 border-2 border-fuchsia-600 bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-800 font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center gap-1">
            🔁 Re-cascade all statuses
          </button>
          <button
            data-testid="clickup-migrate-photos-btn"
            onClick={async () => {
              if (!window.confirm("Migrate legacy `photos[]` URLs into the correct `after_photos[]` bucket so they render on JobDetail?")) return;
              try {
                toast.info("Migrating photos…");
                const r = await axios.post(`${BASE}/api/clickup/mirror/migrate-photos-field`, {},
                  { headers: { Authorization: `Bearer ${token()}` } });
                toast.success(`Moved ${r.data.photos_moved} photo(s) across ${r.data.jobs_migrated} WO(s)`);
                load();
              } catch (e) {
                toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Migration failed"));
              }
            }}
            className="h-9 px-3 border-2 border-teal-500 bg-teal-50 hover:bg-teal-100 text-teal-800 font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center gap-1">
            🖼️ Migrate legacy photos
          </button>
          <button
            data-testid="clickup-recascade-rollback-btn"
            onClick={async () => {
              if (!window.confirm("EMERGENCY ROLLBACK — revert every WO wrongly marked 'completed' by the last re-cascade run. Undoes those specific writes only. Continue?")) return;
              try {
                toast.info("Rolling back re-cascade…");
                const r = await axios.post(`${BASE}/api/clickup/mirror/recascade-rollback`, {},
                  { headers: { Authorization: `Bearer ${token()}` } });
                toast.success(`Reverted ${r.data.reverted} WOs · check the Jobs Board`);
                load();
              } catch (e) {
                toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Rollback failed"));
              }
            }}
            className="h-9 px-3 border-2 border-red-600 bg-red-50 hover:bg-red-100 text-red-800 font-black uppercase text-[10px] tracking-widest rounded inline-flex items-center gap-1">
            ↩︎ Undo re-cascade
          </button>
        </div>
        <div className="mt-1 text-[10px] text-slate-500">
          <b>Re-cascade</b> replays every stored ClickUp status onto <em>all</em> FR WOs that share the same WO# — fixes the case where twin duplicates only got half-updated.
        </div>
        {backfillResult && (
          <div className={`mt-3 p-2 bg-white border rounded text-xs ${backfillResult.status === "done" ? "border-emerald-300" : "border-cyan-300"}`}>
            <b>{backfillResult.status === "done" ? "Done:" : "Running…"}</b>{" "}
            mirrored {backfillResult.mirrored || 0} events · matched {backfillResult.matched_to_wo || 0} to FR WOs · scanned {backfillResult.lists_scanned || 0}
            {backfillResult.lists_total ? `/${backfillResult.lists_total}` : ""} lists
            {backfillResult.status !== "done" && (
              <span className="ml-1 inline-block h-2 w-2 rounded-full bg-cyan-500 animate-pulse"/>
            )}
            {backfillResult.errors?.length > 0 && (
              <div className="mt-1 text-red-700 text-[10px]">
                Errors: {backfillResult.errors.join("; ")}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-4 border-2 border-slate-200 bg-white">
        <div className="font-black uppercase text-[11px] tracking-widest text-slate-700 mb-2">
          Recent mirrored events {status?.recent?.length ? `(${status.recent.length})` : ""}
        </div>
        {!status?.recent?.length && (
          <div className="p-6 text-center text-xs text-slate-400 italic border-2 border-dashed border-slate-200">
            No events yet. Change a task status or drop a comment in ClickUp — you'll see it here.
          </div>
        )}
        {status?.recent?.length > 0 && (
          <div className="divide-y divide-slate-100 border-2 border-slate-100 bg-slate-50/40">
            {status.recent.map((r, i) => (
              <div key={i} className="p-2 text-xs flex items-start gap-2">
                <span className={`inline-flex items-center h-5 px-1.5 font-black uppercase text-[9px] tracking-widest border ${
                  r.kind === "status" ? "border-cyan-400 bg-cyan-50 text-cyan-800" :
                  r.kind === "comment" ? "border-fuchsia-400 bg-fuchsia-50 text-fuchsia-800" :
                  r.kind === "deleted" ? "border-rose-400 bg-rose-50 text-rose-800" :
                  "border-slate-300 bg-slate-100 text-slate-700"
                }`}>{r.kind}</span>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-slate-500 text-[10px]">
                    {r.at ? new Date(r.at).toLocaleString() : ""}
                  </div>
                  <div className="font-bold text-slate-800 truncate">{r.cu_task}</div>
                  {r.detail && <div className="text-slate-600 truncate">{r.detail}</div>}
                </div>
                {r.fr_job_id && (
                  <a href={`/admin/jobs/${r.fr_job_id}`} target="_blank" rel="noreferrer"
                    className="text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:underline flex-none">
                    Open WO →
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ClickUpIdentityPanel() {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const [me, setMe] = useState(null);
  const [cuEmail, setCuEmail] = useState("");
  const [cuUser, setCuUser] = useState("");
  const [pt, setPt] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPt, setShowPt] = useState(false);
  const [pulseBind, setPulseBind] = useState(false);
  const tokenInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${BASE}/api/auth/me`, auth());
      setMe(data);
      setCuEmail(data.clickup_email || data.email || "");
      setCuUser(data.clickup_username || data.name || "");
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  // Deep-link from Team Chat's yellow banner (`/admin/clickup?bind=1#personal-token`)
  // — auto-scroll to the token input, focus it, and pulse the border once so
  // the user's eye lands exactly where the paste needs to happen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("bind") !== "1") return;
    const t = setTimeout(() => {
      const el = tokenInputRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus();
      setPulseBind(true);
      setTimeout(() => setPulseBind(false), 2400);
    }, 300);
    return () => clearTimeout(t);
  }, [me]);

  const save = async () => {
    if (!me) return;
    setBusy(true);
    try {
      const payload = {
        clickup_email: cuEmail.trim().toLowerCase(),
        clickup_username: cuUser.trim(),
      };
      if (pt) payload.clickup_personal_token = pt.trim();
      await axios.patch(`${BASE}/api/users/${me.id}`, payload, auth());
      toast.success("ClickUp identity saved");
      setPt(""); // never keep the token in the field
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const clearToken = async () => {
    if (!window.confirm("Remove your ClickUp personal token? Replies will fall back to the workspace bot with a `[Your Name from Field Radar]` prefix.")) return;
    try {
      await axios.patch(`${BASE}/api/users/${me.id}`, { clickup_personal_token: "" }, auth());
      toast.success("Personal token cleared");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!me) return null;

  return (
    <div className="p-4 border-2 border-indigo-200 bg-indigo-50" data-testid="clickup-identity-panel">
      <div className="font-black text-xs uppercase tracking-widest text-indigo-900">
        🔗 My ClickUp Identity
      </div>
      <div className="text-xs text-indigo-800 mt-0.5 max-w-2xl">
        Bind your ClickUp login so comments authored by you in ClickUp appear in your private
        <b> #my-clickup </b> Team Chat channel. Nobody else sees your feed.
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-indigo-800">
            ClickUp email
          </label>
          <input type="email" value={cuEmail} onChange={e => setCuEmail(e.target.value)}
            data-testid="cu-identity-email"
            placeholder="you@example.com"
            className="mt-1 w-full h-10 px-3 border-2 border-[#0f172a] text-sm"/>
          <div className="text-[10px] text-indigo-600 mt-1">
            {me.email === cuEmail.trim().toLowerCase() ? "✓ Same as your Field Radar email — auto-matched" : "Different from your FR login — that's fine, we'll match on this"}
          </div>
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-indigo-800">
            ClickUp display name (optional)
          </label>
          <input type="text" value={cuUser} onChange={e => setCuUser(e.target.value)}
            data-testid="cu-identity-username"
            placeholder="Andrew Harman"
            className="mt-1 w-full h-10 px-3 border-2 border-[#0f172a] text-sm"/>
        </div>
      </div>
      <div id="personal-token" className={`mt-3 p-3 border-2 border-dashed bg-white transition-all ${pulseBind ? "border-amber-500 bg-amber-50 ring-4 ring-amber-200 animate-pulse" : "border-indigo-300"}`}>
        <div className="text-[10px] font-black uppercase tracking-widest text-indigo-900">
          🔒 Personal API token (optional — enables two-way replies)
        </div>
        <div className="text-[11px] text-slate-600 mt-1 max-w-xl">
          Get one at <span className="font-mono">ClickUp → Settings → Apps → API Token → Generate</span>. Starts with <span className="font-mono">pk_</span>. Without it, replies still work but show as <span className="italic">[Your Name from Field Radar]</span> under the workspace bot.
        </div>
        <div className="mt-2 flex gap-2">
          <input ref={tokenInputRef} type={showPt ? "text" : "password"} value={pt} onChange={e => setPt(e.target.value)}
            data-testid="cu-identity-personal-token"
            placeholder={me.has_clickup_personal_token ? "•••••• (personal token on file — paste a new one to replace)" : "pk_XXXXXXXXXXXXXXXXX"}
            className="flex-1 h-10 px-3 border-2 border-[#0f172a] text-sm font-mono"/>
          <button onClick={() => setShowPt(!showPt)} type="button"
            className="h-10 px-3 border-2 border-slate-300 hover:bg-slate-50 text-xs">
            {showPt ? "Hide" : "Show"}
          </button>
        </div>
        {me.has_clickup_personal_token && (
          <button onClick={clearToken} type="button"
            data-testid="cu-identity-clear-token"
            className="mt-2 text-[10px] font-black uppercase tracking-widest text-rose-700 hover:underline">
            × Remove personal token
          </button>
        )}
      </div>
      <div className="mt-3 flex justify-end">
        <button onClick={save} disabled={busy}
          data-testid="cu-identity-save"
          className="h-10 px-5 bg-indigo-700 hover:bg-indigo-800 text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-40">
          {busy ? "Saving…" : "Save Identity"}
        </button>
      </div>
    </div>
  );
}

